import {
  JSNS,
  type Attr,
  type Identity,
  type JsonObject,
  type PrefBag,
  type Signature,
  type SignatureContent,
} from '@zimbra-webif/types';
import type { ZimbraClient } from '../client';

function asArray<T>(v: T[] | T | undefined): T[] {
  if (v === undefined) return [];
  return Array.isArray(v) ? v : [v];
}

/**
 * Flatten an identity's `<a name=...>` array into a typed `_attrs` bag while
 * keeping the raw `a` array intact (doc Open #7: normalize-in, re-serialize-out).
 */
function normalizeIdentity(raw: Identity): Identity {
  const attrs: PrefBag = {};
  for (const a of asArray(raw.a)) {
    if (!a.name) continue;
    const value = a._content ?? '';
    const existing = attrs[a.name];
    if (existing === undefined) {
      attrs[a.name] = value;
    } else if (Array.isArray(existing)) {
      existing.push(value);
    } else {
      attrs[a.name] = [existing, value];
    }
  }
  return { ...raw, _attrs: attrs };
}

/** Build the `<a name=...>` array from a flat attrs bag. */
function attrsToArray(attrs: PrefBag): Attr[] {
  const out: Attr[] = [];
  for (const [name, value] of Object.entries(attrs)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      for (const v of value) out.push({ name, _content: v });
    } else {
      out.push({ name, _content: value });
    }
  }
  return out;
}

/** Input for creating/modifying an identity. */
export interface IdentityInput {
  name?: string;
  /** flat `zimbraPref*` attrs (serialized to the `<a name>` array on the wire) */
  attrs: PrefBag;
}

/** Input for creating/modifying a signature. */
export interface SignatureInput {
  name?: string;
  /** content parts; provide plain and/or html */
  content?: SignatureContent[];
  /** convenience: plain-text body (becomes a text/plain content part) */
  text?: string;
  /** convenience: html body (becomes a text/html content part) */
  html?: string;
  /** inline-image content-id */
  cid?: string;
}

function buildSignaturePayload(input: SignatureInput): JsonObject {
  const content: SignatureContent[] = [...(input.content ?? [])];
  if (input.text !== undefined) content.push({ type: 'text/plain', _content: input.text });
  if (input.html !== undefined) content.push({ type: 'text/html', _content: input.html });
  const sig: JsonObject = {};
  if (input.name !== undefined) sig.name = input.name;
  if (content.length > 0) sig.content = content;
  if (input.cid !== undefined) sig.cid = { _content: input.cid };
  return sig;
}

/**
 * Identities (send-as personas) and Signatures (urn:zimbraAccount).
 *
 * One public method per SOAP command; see docs/features/account-prefs-security.md.
 */
export class IdentitiesApi {
  constructor(private readonly client: ZimbraClient) {}

  /* ---------------------------------------------------------------------- */
  /* Identities                                                              */
  /* ---------------------------------------------------------------------- */

  /** GetIdentitiesRequest — all send-as personas (normalized `_attrs` bag added). */
  async getIdentities(): Promise<Identity[]> {
    const raw = await this.client.call<{ identity?: Identity[] | Identity }>(
      JSNS.account,
      'GetIdentities',
      {},
    );
    return asArray(raw.identity).map(normalizeIdentity);
  }

  /** CreateIdentityRequest — echoes the created identity (with its new `id`). */
  async createIdentity(input: IdentityInput): Promise<Identity> {
    const identity: JsonObject = { a: attrsToArray(input.attrs) };
    if (input.name !== undefined) identity.name = input.name;
    const raw = await this.client.call<{ identity?: Identity[] | Identity }>(
      JSNS.account,
      'CreateIdentity',
      { identity },
    );
    const created = asArray(raw.identity)[0];
    return created ? normalizeIdentity(created) : ({ a: [] } as Identity);
  }

  /** ModifyIdentityRequest — update an identity selected by id. */
  async modifyIdentity(id: string, attrs: PrefBag): Promise<JsonObject> {
    return this.client.call(JSNS.account, 'ModifyIdentity', {
      identity: { id, a: attrsToArray(attrs) },
    });
  }

  /**
   * DeleteIdentityRequest — delete by id or by name (selector chosen on which
   * key is provided).
   */
  async deleteIdentity(sel: { id: string } | { name: string }): Promise<JsonObject> {
    return this.client.call(JSNS.account, 'DeleteIdentity', { identity: { ...sel } });
  }

  /* ---------------------------------------------------------------------- */
  /* Signatures                                                              */
  /* ---------------------------------------------------------------------- */

  /** GetSignaturesRequest — all named signatures. */
  async getSignatures(): Promise<Signature[]> {
    const raw = await this.client.call<{ signature?: Signature[] | Signature }>(
      JSNS.account,
      'GetSignatures',
      {},
    );
    return asArray(raw.signature);
  }

  /** CreateSignatureRequest — echoes the created signature (with its new `id`). */
  async createSignature(input: SignatureInput): Promise<Signature> {
    const raw = await this.client.call<{ signature?: Signature[] | Signature }>(
      JSNS.account,
      'CreateSignature',
      { signature: buildSignaturePayload(input) },
    );
    return asArray(raw.signature)[0] ?? {};
  }

  /** ModifySignatureRequest — update a signature selected by id. */
  async modifySignature(id: string, input: Omit<SignatureInput, 'name'> & { name?: string }): Promise<JsonObject> {
    const signature = buildSignaturePayload(input);
    signature.id = id;
    return this.client.call(JSNS.account, 'ModifySignature', { signature });
  }

  /** DeleteSignatureRequest — delete by id or by name. */
  async deleteSignature(sel: { id: string } | { name: string }): Promise<JsonObject> {
    return this.client.call(JSNS.account, 'DeleteSignature', { signature: { ...sel } });
  }
}
